import React from 'react';

import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedSelect from '@portal/components/AdvancedSelect/AdvancedSelect';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import {
  type CardControlOptions,
  type ModalState,
  TypeModalEnum,
} from '@portal/hooks/useCard';
import { CardControlEnum, ReasonType } from '@portal/models/enumerators/cards';
import { translate } from '@portal/services/i18n';
import './CardActionsModal.less';

type CancelOption = {
  name: string;
  value: ReasonType;
};

type CardActionsModalProps = {
  modalTypeActions: ModalState;
  onClose: () => void;
  onConfirm: () => void;
  disabled: boolean;
  title: string;
  description: string;
  cancelDescription: ReasonType;
  onCancelDescriptionChange: (value: ReasonType) => void;
  cancelOptions: CancelOption[];
  password: string;
  onPasswordChange: (value: string) => void;
  isFirstAccess: boolean;
  cardControlOptions: CardControlOptions;
  cardDetails: bank.Card | null;
};

const CardActionsModal = ({
  modalTypeActions,
  onClose,
  onConfirm,
  disabled,
  title,
  description,
  cancelDescription,
  onCancelDescriptionChange,
  cancelOptions,
  password,
  onPasswordChange,
  isFirstAccess,
  cardControlOptions,
  cardDetails,
}: CardActionsModalProps) => {
  const renderContent = () => {
    switch (modalTypeActions.type) {
      case TypeModalEnum.CANCEL:
      case TypeModalEnum.DUPLICATE:
        return (
          <AdvancedSelect
            testId="card-cancel-description-input"
            label={translate(
              'PAGES.CARDS.MY_CARDS.DETAILS.CANCEL_CARD.REASON_LABEL'
            )}
            value={cancelDescription}
            onChange={(value: ReasonType) => onCancelDescriptionChange(value)}
            options={cancelOptions}
          />
        );
      case TypeModalEnum.CHANGE_PASSWORD:
        return (
          <AdvancedInput
            testId="card-change-password-input"
            label={translate(
              'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.PASSWORD_LABEL'
            )}
            type="password"
            value={password}
            maxLength={4}
            helperText={`${password.length}/4`}
            onChange={(value: string) => onPasswordChange(value)}
            inputMode="numeric"
          />
        );
      case TypeModalEnum.UNBLOCK:
        if (!isFirstAccess) return null;

        return (
          <>
            {['ONE', 'TWO', 'THREE'].map((step) => (
              <ThemedText
                key={step}
                variant="body"
                className="card-actions-modal__text"
              >
                {translate(
                  `PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.${step}`
                )}
              </ThemedText>
            ))}
          </>
        );
      case TypeModalEnum.CARD_CONTROL: {
        const { feature } = cardControlOptions;

        const contentByFeature = {
          [CardControlEnum.CONTACTLESS]: {
            intro: 'PAGES.CARDS.MY_CARDS.CARD_CONTROL.CONTACTLESS.TITLE',
            description: cardDetails?.contactlessEnabled
              ? 'PAGES.CARDS.MY_CARDS.CARD_CONTROL.CONTACTLESS.DISABLED_DESCRIPTION'
              : 'PAGES.CARDS.MY_CARDS.CARD_CONTROL.CONTACTLESS.DESCRIPTION',
          },
          [CardControlEnum.INTERNATIONAL]: {
            intro:
              'PAGES.CARDS.MY_CARDS.CARD_CONTROL.INTERNATIONAL_SHOPPING.TITLE',
            description: cardDetails?.internationalEnabled
              ? 'PAGES.CARDS.MY_CARDS.CARD_CONTROL.INTERNATIONAL_SHOPPING.DISABLED_DESCRIPTION'
              : 'PAGES.CARDS.MY_CARDS.CARD_CONTROL.INTERNATIONAL_SHOPPING.DESCRIPTION',
          },
        };

        const currentContent = contentByFeature[feature];

        if (!currentContent) return null;

        return (
          <div className="card-actions-modal__card-control">
            <ThemedText variant="body">
              {translate(currentContent.intro)}
            </ThemedText>
            <ThemedText variant="body">
              {translate(currentContent.description)}
            </ThemedText>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <GenericModal
      visible={modalTypeActions.visible}
      onClose={onClose}
      title={title}
      description={description}
      actions={[
        {
          label: translate('PAGES.CARDS.MY_CARDS.DETAILS.CANCEL'),
          onClick: onClose,
          variant: 'outlined',
        },
        {
          label: translate('PAGES.CARDS.MY_CARDS.DETAILS.CONFIRM'),
          onClick: onConfirm,
          variant: 'primary',
          disabled,
        },
      ]}
    >
      {renderContent()}
    </GenericModal>
  );
};

export default CardActionsModal;
